import { UserAgentApplication } from 'msal';
import { b2cPolicies } from './policies';
import { apiConfig } from './apiConfig';
import { msalConfig, loginRequest, tokenRequest } from './authConfig';
import { callApiWithAccessToken } from './api';
import { SuccessAuthCallback, ErrorAuthCallback, IAuth, IAuthProps } from './types';

export class AuthRedirect implements IAuth {
  MSALObj: any = null;
  accessToken: any = null;
  successAuthCallback: SuccessAuthCallback;
  errorAuthCallback: ErrorAuthCallback;

  constructor(props: IAuthProps) {
    this.successAuthCallback = props.successAuthCallback;
    this.errorAuthCallback = props.errorAuthCallback;
    this.initialize();
  }

  initialize = () => {
    // Create the main MSALObj instance
    // configuration parameters are located at authConfig.js
    this.MSALObj = new UserAgentApplication(msalConfig as any);

    this.accessToken = null;

    // Register Callbacks for Redirect flow
    this.MSALObj.handleRedirectCallback(this.authRedirectCallBack);
  };

  authRedirectCallBack = (error: any, response: any) => {
    // Error handling
    if (error) {
      // Check for forgot password error
      // Learn more about AAD error codes at https://docs.microsoft.com/en-us/azure/active-directory/develop/reference-aadsts-error-codes
      if (error.errorMessage.indexOf('AADB2C90118') > -1) {
        try {
          // Password reset policy/authority
          this.MSALObj.loginRedirect(b2cPolicies.authorities.forgotPassword);
        } catch (err) {}
      }
    } else {
      // We need to reject id tokens that were not issued with the default sign-in policy.
      // "acr" claim in the token tells us what policy is used (NOTE: for new policies (v2.0), use "tfp" instead of "acr")
      // To learn more about b2c tokens, visit https://docs.microsoft.com/en-us/azure/active-directory-b2c/tokens-overview
      if (response.tokenType === 'id_token' && response.idToken.claims['tfp'] !== b2cPolicies.names.signUpSignIn) {
        this.MSALObj.logout();
      } else if (response.tokenType === 'id_token' && response.idToken.claims['tfp'] === b2cPolicies.names.signUpSignIn) {
        if (this.MSALObj.getAccount()) {
          if (!response.accessToken && response?.idToken?.rawIdToken) {
            this.setAccessToken(response.idToken.rawIdToken);
          }
          this.successAuthCallback();
        }
      } else if (response.tokenType === 'access_token') {
        this.setAccessToken(response.accessToken);
        if (this.accessToken) {
          try {
            callApiWithAccessToken(apiConfig.webApi, this.accessToken);
          } catch (err) {}
        }
      }
    }
  };

  getAccount() {
    return this.MSALObj.getAccount();
  }

  signIn = () => {
    this.MSALObj.loginRedirect(loginRequest);
  };

  // sign-out the user
  logout = async () => {
    this.setAccessToken(null);
    // Removes all sessions, need to call AAD endpoint to do full logout
    return await this.MSALObj.logoutAsync();
  };

  setAccessToken = (token: any) => {
    this.accessToken = token;
  };

  getAccessToken = () => this.accessToken;

  getToken = (request: any = tokenRequest) => this.MSALObj.acquireTokenSilent(request);

  // main method to get token with redirect flow
  getTokenRedirect = (request: any) => {
    return this.getToken(request)
      .then((response: any) => {
        if (response.accessToken) {
          this.setAccessToken(response.accessToken);

          if (this.accessToken) {
            try {
              callApiWithAccessToken(apiConfig.webApi, this.accessToken);
            } catch (err) {}
          }
        }
      })
      .catch((error: any) => {
        // fallback to interaction when silent call fails
        return this.MSALObj.acquireTokenRedirect(request);
      });
  };

  // calls the resource API with the token
  passTokenToApi = () => {
    if (!this.accessToken) {
      this.getTokenRedirect(tokenRequest);
    } else {
      try {
        callApiWithAccessToken(apiConfig.webApi, this.accessToken);
      } catch (err) {}
    }
  };
}
